import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { Field, FieldProps, useFormikContext } from 'formik';
import { SelectValue } from 'antd/lib/select';
import Checkbox, { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { Col, Row } from 'antd/lib/grid';

import { Nullable, isPresent } from '@common/typescript/objects/Nullable';
import { FormikInput } from '@common/react/components/Forms/FormikInput/FormikInput';

import { List } from '@common/typescript/objects/List';

import { ApplicationState } from '@app/store';
import { bindItemsActionCreators } from '@app/store/ItemList';
import {
	PetOnHold,
	ServiceType,
} from '@app/objects/Pet';
import { Location } from '@app/objects/Location';

import { LocalSelect, Option } from '@app/components/UI/Inputs/LocalSelect';
import { LocationSelect } from '@app/components/UI/Inputs/LocationSelect';
import { getCardFields, Card } from '@app/components/UI/Cards/Card';
import { DeliveryTypeSelect } from '@app/components/UI/Inputs/DeliveryTypes/DeliveryTypeSelect';
import { FormValues } from '@app/components/Pages/ClinicPetEditor/types';
import {
	BaseSectionProps,
	getCremationInfo,
} from '@app/components/Pages/ClinicPetEditor/ClinicPetsComponents/ViewContent';
import { ClinicSelect } from '@app/components/UI/Inputs/ClinicSelect';
import { ClinicSelectInfo } from '@app/objects/Clinic';
import { isPickDefault } from '@app/store/SelectList/SelectsInterfaces';
import { FormikField } from '@app/components/Forms/FormikField';
import { useCrematoryFeatures } from '@app/hooks/Crematory/useCrematoryFeatures';
import { DeliveryEntitySelect } from '@app/components/UI/Inputs/DeliveryTypes/DeliveryEntitySelect';
import { useDeliveryManager } from '@app/hooks/Delivery/useDeliveryManager';
import { Price, PriceType, PriceKind } from '@app/objects/Price';
import { useRequest } from '@app/hooks/useRequest';
import { LoadPricesParams } from '@app/components/Pages/PetEditor/Editor';
import { iKey } from '@app/components/Pages/PetEditor/Services';
import { ClinicCrematory } from '@app/objects/ClinicCrematory';
import { PetPricePresetSelect } from '@app/components/UI/Inputs/PricePresetSelect';
import { ServiceTypeOption, useServiceTypes, useServiceTypeNames } from '@app/hooks/Crematory/useServiceTypes';

function preselect(items: Array<Location>): number | undefined {
	if (items.length === 1) return items[0].id;

	return undefined;
}

type ColumnProps = Pick<React.PropsWithChildren<unknown>, 'children'>;
interface PriceListRequestParams {
	crematoryId: number;
	clinicId: Array<Nullable<number>>;
	priceType: PriceType;
	serviceType: ServiceType;
	withSpecialService: boolean;
	withInventoryItem: boolean;
	count: number;
	filters?: {
		priceKind?: Array<PriceKind>;
	};
}

function loadPrices(
	params: LoadPricesParams,
	onReload: (data?: PriceListRequestParams | undefined) => Promise<List<Price> | void>,
) {
	onReload({
		crematoryId: params.crematoryId,
		clinicId: [params.clinicId, null],
		priceType: params.priceType || PriceType.Wholesale,
		serviceType: params.serviceType,
		withSpecialService: true,
		withInventoryItem: true,
		count: 500,

		filters: params.priceKind === undefined
			? undefined
			: {
				priceKind: [params.priceKind],
			},
	});
}

const Column: React.FC<ColumnProps> = (props: ColumnProps) => <Col xs={24} lg={12}>{props.children}</Col>;

function getDeliveryPrices(list: Array<Price>): Array<Price> {
	const source = list.filter((item: Price) => item.priceKind === PriceKind.DeliveryPrice);
	if (source.some((item: Price) => item.clinicId !== null)) return source.filter((item: Price) => item.clinicId !== null);

	return source.filter((item: Price) => item.clinicId === null);
}

const defaultAddress = {
	streetAddress: '',
	streetAddress2: '',
	zip: '',
	city: '',
	stateId: null,
};

export const CremationDetails: React.FC<Omit<BaseSectionProps, 'crematoryId'>> = ({ crematory, mode }: Omit<BaseSectionProps, 'crematoryId'>) => {
	const { t } = useTranslation();
	const { values, setValues } = useFormikContext<FormValues>();
	const clinic = useSelector((state: ApplicationState) => state.clinic.item);
	const clinicLocations = useSelector((state: ApplicationState) => state.clinicLocations.items);
	const link = clinic?.clinicCrematories?.find((item: ClinicCrematory) => item.clinicId === clinic.id);
	const dispatch = useDispatch();
	const factory = React.useMemo(() => bindItemsActionCreators<Location>(dispatch), [dispatch]);
	const features = useCrematoryFeatures();
	const service = useDeliveryManager();

	const priceListRequest = useRequest<List<Price>, PriceListRequestParams>('priceList', undefined, { requestOnMount: false });
	const onLoadPrices = React.useCallback((params: LoadPricesParams) => loadPrices(params, priceListRequest.reload), []);
	const prices = getDeliveryPrices(priceListRequest.item?.list ?? []);
	const serviceNames = useServiceTypeNames();
	const serviceTypes: Array<Option> = useServiceTypes().map((item: ServiceTypeOption) => ({
		text: item.label,
		value: item.value,
	}));
	const clinics = useSelector((state: ApplicationState) => state.clinics.items);

	React.useEffect(() => {
		factory.reqPages('clinicLocations', 'locationList', 'location', {
			objectId: clinic?.id,
			getMine: true,
			count: 20,
			offset: 0,
		});
	}, [factory, clinic?.id]);

	React.useEffect(() => {
		if (values.deliveryClinicId && (!clinics || clinics.length === 0) && crematory?.id) {
			factory.reqPages('clinics', 'clinicList', 'clinic', {
				crematoryId: crematory.id,
				count: 100,
				offset: 0,
			});
		}
	}, [values.deliveryClinicId, clinics, crematory?.id]);

	React.useEffect(() => {
		const crematoryId = features?.item?.crematoryId;

		if (!crematoryId) return;
		if (values.serviceType === undefined) return;

		onLoadPrices({
			crematoryId,
			serviceType: values.serviceType,
			priceType: PriceType.Wholesale,
			clinicId: clinic?.id ?? null,
			priceKind: PriceKind.DeliveryPrice,
		});
	}, [clinic?.id, values.serviceType, features?.item?.crematoryId]);

	return (
		<Card
			name="Cremation information"
			mode={mode}
			fullWidth

			editContent={(
				<Row gutter={[30, 16]}>
					<Column>
						<FormikField
							fieldName="serviceType"
							title="Cremation Type*"
							render={({ field, form }: FieldProps<SelectValue, FormValues>) => (
								<LocalSelect
									onChange={(value: ServiceType) => {
										form.setValues((values: FormValues) => ({
											...values,

											serviceType: value,
											services: [],
											urns: [],
											products: [],
										}));
									}}
									value={typeof field.value === 'number' && field.value >= 0 ? field.value : undefined}
									options={serviceTypes}
									fieldName={field.name}
									deselectType={field.name}
									filterName={field.name}
									allowClear={false}
									placeholder="Select cremation type"
								/>
							)}
						/>
					</Column>
					{clinicLocations && clinicLocations.length > 0 && (
						<Column>
							<Field name="clinicLocationId">
								{(fieldProps: FieldProps<FormValues>) => (
									<FormikInput
										fieldProps={fieldProps}
										title="Clinic location"
										containerClassName=""
										render={({ field, form }: FieldProps<Nullable<number>, FormValues>) => (
											<LocationSelect
												value={field.value ?? undefined}
												onChange={(value: Nullable<number>) => form.setFieldValue('clinicLocationId', value, false)}

												preselect={field.value ? undefined : preselect}
												disabled={(items: Array<Location>) => items.length === 0}

												forMyClinic
											/>
										)}
									/>
								)}
							</Field>
						</Column>
					)}
					<Field name="deliveryTypeId">
						{
							(fieldProps: FieldProps<Nullable<number>, FormValues>) => {
								if (!clinic?.canSetDeliveryType) return null;
								if (!features.item?.enableCustomDeliveryType) return null;

								const crematoryId = features?.item?.crematoryId;
								if (!crematoryId) return null;

								return (
									<Column>
										<FormikInput
											fieldProps={fieldProps}
											containerClassName=""
											title="Delivery Type"
											render={() => (
												<DeliveryEntitySelect
													value={fieldProps.field.value}
													onChange={(value: Nullable<number>) => {
														fieldProps.form.setValues((current: FormValues) => {
															const values = { ...current };

															values.deliveryTypeId = value;
															values.deliveryAddress = '';
															values.deliveryAddress2 = '';
															values.deliveryZip = '';
															values.deliveryCity = '';
															values.deliveryStateId = null;
															values.deliveryState = null;

															if (!service.isClinic(value)) {
																values.deliveryClinicId = null;
																values.deliveryLocationId = null;
															} else {
																values.deliveryClinicId = link?.deliveryClinicId ?? null;
																values.deliveryLocationId = link?.deliveryLocationId ?? null;
															}

															return values;
														});
													}}
													crematoryId={crematoryId}
													prices={prices}
													disabled={fieldProps.form.values.serviceType === undefined}
												/>
											)}
										/>
									</Column>
								);
							}
						}
					</Field>
					<Field name="deliveryType">
						{
							(fieldProps: FieldProps<FormValues>) => {
								if (!clinic?.canSetDeliveryType) return null;
								if (features.item?.enableCustomDeliveryType) return null;

								return (
									<Column>
										<FormikInput
											fieldProps={fieldProps}
											containerClassName=""
											title="Delivery Type"
											render={({ field, form }: FieldProps<number, FormValues>) => (
												<DeliveryTypeSelect
													value={field.value}
													prices={prices}
													onChange={(value) => {
														form.setValues((current: FormValues) => {
															const values = { ...current };

															values.deliveryType = value;
															values.deliveryAddress = '';
															values.deliveryAddress2 = '';
															values.deliveryZip = '';
															values.deliveryCity = '';
															values.deliveryStateId = null;
															values.deliveryState = null;

															if (!service.isClinic(value)) {
																values.deliveryClinicId = null;
																values.deliveryLocationId = null;
															} else {
																values.deliveryClinicId = link?.deliveryClinicId ?? null;
																values.deliveryLocationId = link?.deliveryLocationId ?? null;
															}

															return values;
														});
													}}
													disabled={fieldProps.form.values.serviceType === undefined}
												/>
											)}
										/>
									</Column>
								);
							}
						}
					</Field>
					<Field name="deliveryClinicId">
						{(fieldProps: FieldProps<FormValues>) => {
							if (!clinic?.canSetDeliveryType) return null;
							if (!link?.canSelectDeliveryClinic) return null;

							const deliveryType = features.item?.enableCustomDeliveryType
								? fieldProps.form.values.deliveryTypeId
								: fieldProps.form.values.deliveryType;
							if (!service.isClinic(deliveryType)) return null;

							return (
								<Column>
									<FormikInput
										fieldProps={fieldProps}
										containerClassName=""
										title="Delivery Clinic"
										render={({ field, form }: FieldProps<number, FormValues>) => {
											return (
												<ClinicSelect
													value={field.value}
													crematoryId={crematory?.id}
													onChange={(id?: number) => {
														form.setValues((current: FormValues) => {
															const values = { ...current };

															values.deliveryClinicId = id ?? null;
															values.deliveryLocationId = null;

															if (!id) {
																values.deliveryAddress = '';
																values.deliveryAddress2 = '';
																values.deliveryZip = '';
																values.deliveryCity = '';
																values.deliveryStateId = null;
																values.deliveryState = null;
																values.deliveryClinicId = null;
																values.deliveryLocationId = null;
															}

															return values;
														});
													}}
													pickDefault={(store: Array<ClinicSelectInfo>) => isPickDefault(store)}
													placeholder="Select the Custom Delivery Clinic"
													allowClear={false}
												/>
											);
										}}
									/>
								</Column>
							);
						}}
					</Field>
					<Field name="deliveryLocationId">
						{(fieldProps: FieldProps<FormValues>) => {
							if (!clinic?.canSetDeliveryType) return null;
							if (!link?.canSelectDeliveryClinic) return null;

							const deliveryType = features.item?.enableCustomDeliveryType
								? fieldProps.form.values.deliveryTypeId
								: fieldProps.form.values.deliveryType;
							if (!service.isClinic(deliveryType)) return null;

							return (
								<Column>
									<FormikInput
										fieldProps={fieldProps}
										containerClassName=""
										title={`${t(iKey('labels.delivery-location'))}`}
										render={({ field, form }: FieldProps<number, FormValues>) => (
											<LocationSelect
												value={field.value ?? undefined}
												disabled={!form.values.deliveryClinicId}
												clinicId={form.values.deliveryClinicId === null ? undefined : +form.values.deliveryClinicId}
												local={false}
												onChange={(value: Nullable<number>, item: Nullable<Location>) => {
													if (!form.values.deliveryClinicId) return;

													const address = item ?? clinic ?? defaultAddress;
													form.setValues({
														...form.values,
														deliveryLocation: null,
														deliveryLocationId: value,

														deliveryAddress: address.streetAddress,
														deliveryAddress2: address.streetAddress2,
														deliveryZip: address.zip,
														deliveryCity: address.city,
														deliveryStateId: address.stateId ?? null,
													});
												}}
											/>
										)}
									/>
								</Column>
							);
						}}
					</Field>
					{
						features.item?.enablePricePreset && isPresent(values.serviceType)
							? (
								<Column>
									<PetPricePresetSelect
										value={values}
										onChange={setValues}

										priceType={PriceType.Wholesale}
										serviceType={values.serviceType}
									/>
								</Column>
							) : null
					}
					<Row gutter={[30, 8]} className="checkbox">
						<Column>
							<Field name="onHold">
								{(fieldProps: FieldProps<FormValues>) => {
									if (!clinic?.canPutOnHold) return null;

									return (
										<FormikInput
											fieldProps={fieldProps}
											title="On hold"
											containerClassName=""
											render={({ field, form }: FieldProps<number, FormValues>) => (
												<Checkbox
													checked={field.value === PetOnHold.ByClinic}
													onChange={(value: CheckboxChangeEvent) => {
														const isOnHold = value.target.checked ? PetOnHold.ByClinic : PetOnHold.None;
														form.setFieldValue(field.name as keyof FormValues, isOnHold, false);
													}}
												>
													Put pet on hold
												</Checkbox>
											)}
										/>
									);
								}}
							</Field>
						</Column>
						<Column>
							<Field name="rush">
								{(fieldProps: FieldProps<FormValues>) => {
									if (!clinic?.canPutOnRush) return null;

									return (
										<FormikInput
											fieldProps={fieldProps}
											title="Rush"
											containerClassName=""
											render={({ field, form }: FieldProps<boolean, FormValues>) => (
												<Checkbox
													checked={field.value}
													onChange={(value: CheckboxChangeEvent) =>
														form.setFieldValue(field.name as keyof FormValues, value.target.checked, false)}
												>
													Urgent pet processing
												</Checkbox>
											)}
										/>
									);
								}}
							</Field>
						</Column>
					</Row>
				</Row>
			)}

			viewContent={(
				<Field name="">
					{({ form }: FieldProps<FormValues>) =>
						getCardFields(getCremationInfo({
							pet: form.values,
							t,
							clinic,
							locations: clinicLocations,
							services: serviceNames,
							clinics,
						}))}
				</Field>
			)}
		/>
	);
};
